// client/src/coms/views/chatview_parts/WhatShallWePlayModal.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ComsModal from '../../components/ComsModal';

const WHAT_SHALL_WE_PLAY_VOTE_PREFIX = '[vox-what-shall-we-play-vote-v1]';
const WHAT_SHALL_WE_PLAY_WINDOW_MS = 5 * 60 * 1000;

function extractSteamAccountCode(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';

  const cleaned = raw.replace(/[?#].*$/, '').trim();

  function parseBareCode(value) {
    const v = String(value || '').trim().replace(/^\/+|\/+$/g, '');
    if (!v) return null;
    if (/^\d{17}$/.test(v)) return v;
    if (/^[A-Za-z0-9_-]{2,64}$/.test(v)) return v;
    return null;
  }

  if (/^\d{17}$/.test(cleaned)) return cleaned;
  if (/^[A-Za-z0-9_-]{2,64}$/.test(cleaned)) return cleaned;

  const directPathMatch = cleaned.match(/(?:^|\/)(profiles|id)\/([^/?#]+)/i);
  if (directPathMatch) {
    const type = String(directPathMatch[1] || '').toLowerCase();
    const code = parseBareCode(directPathMatch[2]);
    if (type === 'profiles') {
      return code && /^\d{17}$/.test(code) ? code : null;
    }
    if (type === 'id') {
      return code;
    }
  }

  let urlToParse = cleaned;
  if (/steamcommunity\.com/i.test(urlToParse) && !/^https?:\/\//i.test(urlToParse)) {
    urlToParse = `https://${urlToParse.replace(/^\/+/, '')}`;
  }

  if (/^https?:\/\//i.test(urlToParse)) {
    try {
      const u = new URL(urlToParse);
      const host = String(u.hostname || '').toLowerCase().replace(/^www\./, '');
      if (host === 'steamcommunity.com') {
        const segs = String(u.pathname || '').split('/').filter(Boolean);
        const type = String(segs[0] || '').toLowerCase();
        const code = parseBareCode(segs[1]);
        if (type === 'profiles') {
          return code && /^\d{17}$/.test(code) ? code : null;
        }
        if (type === 'id') {
          return code;
        }
      }
    } catch {}
  }

  return null;
}

function normalizeOptionalBool(value) {
  if (value === true) return true;
  if (value === false) return false;
  return null;
}

function normalizeSteamProfile(profile) {
  const p = profile && typeof profile === 'object' ? profile : null;
  if (!p) return null;

  return {
    steamid: String(p.steamid || p.steam_id || p.steamID64 || '').trim(),
    personaname: String(p.personaname || p.persona_name || p.steamID || '').trim(),
    profileurl: String(p.profileurl || p.profile_url || '').trim(),
    customurl: String(p.customurl || p.custom_url || '').trim(),
    avatar: String(p.avatar || p.avatarIcon || p.avataricon || '').trim(),
    avatarmedium: String(p.avatarmedium || p.avatarMedium || '').trim(),
    avatarfull: String(p.avatarfull || p.avatarFull || '').trim(),
  };
}

function pickSteamAvatar(profile) {
  const p = normalizeSteamProfile(profile);
  if (!p) return '';
  return p.avatarfull || p.avatarmedium || p.avatar || '';
}

function normalizeCategoryName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ');
}

function getGameCategoryNames(game) {
  const names = Array.isArray(game && game.category_names)
    ? game.category_names
    : Array.isArray(game && game.categories)
      ? game.categories
      : [];

  return names.map((value) => String(value || '').trim()).filter(Boolean);
}

function categoryListHasAny(game, matchers) {
  const names = getGameCategoryNames(game).map(normalizeCategoryName);
  return names.some((name) => {
    return matchers.some((matcher) => {
      if (typeof matcher === 'string') return name.includes(matcher);
      if (matcher instanceof RegExp) return matcher.test(name);
      return false;
    });
  });
}

function getGameSupportState(game) {
  const explicitCoop = normalizeOptionalBool(
    game && (game.supports_coop != null ? game.supports_coop : game.is_coop)
  );
  const explicitMultiplayer = normalizeOptionalBool(
    game && (game.supports_multiplayer != null ? game.supports_multiplayer : game.is_multiplayer)
  );

  const supportsRemotePlayTogether =
    normalizeOptionalBool(game && game.supports_remote_play_together) === true ||
    categoryListHasAny(game, ['remote play together']);

  const supportsOnlineCoop =
    normalizeOptionalBool(game && game.supports_online_coop) === true ||
    categoryListHasAny(game, ['online co-op', 'online co op']);

  const supportsLocalCoop =
    normalizeOptionalBool(game && game.supports_local_coop) === true ||
    categoryListHasAny(game, [
      'local co-op',
      'local co op',
      'shared/split screen co-op',
      'shared/split screen co op',
    ]);

  const supportsLocalMultiplayer =
    normalizeOptionalBool(game && game.supports_local_multiplayer) === true ||
    categoryListHasAny(game, [
      'local multiplayer',
      'shared/split screen',
      'split screen',
      'shared screen',
    ]);

  const derivedCoop =
    supportsRemotePlayTogether ||
    supportsOnlineCoop ||
    supportsLocalCoop ||
    categoryListHasAny(game, ['co-op', 'co op', 'cooperative']);

  const derivedMultiplayer =
    supportsLocalMultiplayer ||
    derivedCoop ||
    categoryListHasAny(game, [
      'multi-player',
      'multiplayer',
      'massively multiplayer',
      'mmo',
      'pvp',
      'player versus player',
    ]);

  const hasCategoryEvidence = getGameCategoryNames(game).length > 0;

  const coop = derivedCoop
    ? true
    : explicitCoop === true
      ? true
      : explicitCoop === false
        ? false
        : hasCategoryEvidence
          ? false
          : null;

  const multiplayer = derivedMultiplayer
    ? true
    : explicitMultiplayer === true
      ? true
      : explicitMultiplayer === false
        ? false
        : hasCategoryEvidence
          ? false
          : null;

  return {
    coop,
    multiplayer,
    onlineCoop: supportsOnlineCoop,
    localCoop: supportsLocalCoop,
    localMultiplayer: supportsLocalMultiplayer,
    remotePlayTogether: supportsRemotePlayTogether,
    lookupState: String((game && game.store_category_lookup_state) || '').trim(),
  };
}

function normalizeOwnedGames(games) {
  if (!Array.isArray(games)) return [];

  return games
    .map((game) => {
      const g = game && typeof game === 'object' ? game : null;
      if (!g) return null;

      const appid = Number(g.appid);
      const playtimeForever = Number(g.playtime_forever);
      const playtime2Weeks = Number(g.playtime_2weeks);
      const categoryNames = getGameCategoryNames(g);
      const support = getGameSupportState({ ...g, category_names: categoryNames });

      return {
        appid: Number.isFinite(appid) ? appid : 0,
        name: String(g.name || '').trim(),
        playtime_forever: Number.isFinite(playtimeForever) ? playtimeForever : 0,
        playtime_2weeks: Number.isFinite(playtime2Weeks) ? playtime2Weeks : 0,
        category_names: categoryNames,
        is_multiplayer: support.multiplayer,
        is_coop: support.coop,
        supports_multiplayer: support.multiplayer,
        supports_coop: support.coop,
        supports_online_coop: support.onlineCoop,
        supports_local_coop: support.localCoop,
        supports_local_multiplayer: support.localMultiplayer,
        supports_remote_play_together: support.remotePlayTogether,
        store_category_lookup_state: support.lookupState,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const aName = String(a.name || '').trim().toLowerCase();
      const bName = String(b.name || '').trim().toLowerCase();
      if (aName && bName) return aName.localeCompare(bName);
      if (aName) return -1;
      if (bName) return 1;
      return Number(a.appid || 0) - Number(b.appid || 0);
    });
}

function formatPlaytime(minutes) {
  const mins = Number(minutes);
  if (!Number.isFinite(mins) || mins <= 0) return '0h';
  if (mins < 60) return `${Math.max(1, Math.round(mins))}m`;

  const hours = mins / 60;
  const rounded = Math.round(hours * 10) / 10;
  if (Math.abs(rounded - Math.round(rounded)) < 0.001) return `${Math.round(rounded)}h`;
  return `${rounded.toFixed(1)}h`;
}

function buildGameKey(game) {
  const appid = Number(game && game.appid);
  const name = String((game && game.name) || '').trim().toLowerCase();
  if (Number.isFinite(appid) && appid > 0) return `appid:${appid}`;
  return `name:${name}`;
}

function gameSupportsMultiplayer(game) {
  return getGameSupportState(game).multiplayer;
}

function gameSupportsCoop(game) {
  return getGameSupportState(game).coop;
}

function getGameFilterScore(game, onlyMultiplayerGames, onlyCoopGames) {
  const multiplayer = gameSupportsMultiplayer(game);
  const coop = gameSupportsCoop(game);

  if (onlyMultiplayerGames && multiplayer === false) return -1;
  if (onlyCoopGames && coop === false) return -1;

  let score = 0;

  if (onlyMultiplayerGames) {
    if (multiplayer === true) score += 2;
    else if (multiplayer === null) score += 1;
  }

  if (onlyCoopGames) {
    if (coop === true) score += 2;
    else if (coop === null) score += 1;
  }

  return score;
}

function buildGameTags(game) {
  const tags = [];
  const support = getGameSupportState(game);

  if (support.coop === true) {
    if (support.remotePlayTogether) tags.push('Remote Play Together');
    else if (support.onlineCoop && support.localCoop) tags.push('Co-op');
    else if (support.onlineCoop) tags.push('Online Co-op');
    else if (support.localCoop) tags.push('Local Co-op');
    else tags.push('Co-op');
  } else if (support.coop === null) {
    tags.push('Co-op unverified');
  }

  if (support.localMultiplayer) {
    tags.push('Local Multiplayer');
  } else if (support.multiplayer === true) {
    tags.push('Multiplayer');
  } else if (support.multiplayer === null) {
    tags.push('Multiplayer unverified');
  }

  return Array.from(new Set(tags));
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function toInt(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function parseIsoMs(value) {
  const n = Date.parse(String(value || ''));
  return Number.isFinite(n) ? n : 0;
}

function padCountdownUnit(value) {
  const n = Math.max(0, toInt(value, 0));
  return String(n).padStart(2, '0');
}

function formatCountdownLabel(msRemaining) {
  const totalSeconds = Math.max(0, Math.ceil(Number(msRemaining || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${padCountdownUnit(seconds)}`;
}

function buildWhatShallWePlaySessionId(payload) {
  const existing = String((payload && payload.sessionId) || '').trim();
  if (existing) return existing;

  const serverId = String((payload && payload.serverId) || '').trim() || '0';
  const channelDbId = String((payload && payload.channelDbId) || '').trim() || '0';
  const starterUserId = String((payload && payload.startedByUserId) || '').trim() || '0';
  return `wsp_${serverId}_${channelDbId}_${starterUserId}`;
}

function buildVotePayload({ payload, selectedGames }) {
  const nowIso = new Date().toISOString();
  const closesAtIso = new Date(Date.now() + WHAT_SHALL_WE_PLAY_WINDOW_MS).toISOString();

  const serverId = String((payload && payload.serverId) || '').trim();
  const channelId = String((payload && payload.channelId) || '').trim();
  const channelDbId = String((payload && payload.channelDbId) || '').trim();
  const userId = String(
    (payload && (payload.userId || payload.meUserId || payload.currentUserId)) || ''
  ).trim();
  const userName = String(
    (payload &&
      (payload.userName || payload.meUserName || payload.currentUserName || payload.displayName)) ||
      ''
  ).trim();

  return {
    version: 1,
    type: 'what-shall-we-play-vote',
    sessionId: buildWhatShallWePlaySessionId(payload),
    serverId,
    channelId,
    channelDbId,
    userId,
    userName,
    createdAt: nowIso,
    closesAt: closesAtIso,
    games: safeArray(selectedGames).map((game) => ({
      appid: toInt(game && game.appid, 0) || 0,
      name: String((game && game.name) || '').trim(),
    })),
  };
}

async function apiSteamGetMe() {
  const res = await fetch('/api/coms/me/steam', {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  });

  const data = await res.json().catch(() => null);

  if (!res.ok || !data || !data.ok) {
    const msg = (data && data.error) || `GET /api/coms/me/steam failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  return data;
}

async function apiSteamSetMe(steamId) {
  const res = await fetch('/api/coms/me/steam', {
    method: 'PUT',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ steam_id: steamId || null }),
  });

  const data = await res.json().catch(() => null);

  if (!res.ok || !data || !data.ok) {
    const msg = (data && data.error) || `PUT /api/coms/me/steam failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  return data;
}

async function apiSendWhatShallWePlayVote(channelDbId, votePayload) {
  const cid = toInt(channelDbId, 0);
  if (!cid) {
    throw new Error('Missing voice channel id');
  }

  const body = `${WHAT_SHALL_WE_PLAY_VOTE_PREFIX}${JSON.stringify(votePayload)}`;

  const res = await fetch(`/api/coms/voice-channels/${encodeURIComponent(String(cid))}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      body,
      font: 'inherit',
    }),
  });

  const data = await res.json().catch(() => null);

  if (!res.ok || !data || !data.ok) {
    const msg = (data && data.error) || `POST /api/coms/voice-channels/${cid}/messages failed (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }

  return data;
}

export default function WhatShallWePlayModal({ open, payload, onClose }) {
  const [steamLoading, setSteamLoading] = useState(false);
  const [steamSaving, setSteamSaving] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [steamError, setSteamError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [steamId, setSteamId] = useState('');
  const [steamInput, setSteamInput] = useState('');
  const [steamProfile, setSteamProfile] = useState(null);
  const [ownedGames, setOwnedGames] = useState([]);
  const [ownedGamesEnabled, setOwnedGamesEnabled] = useState(true);
  const [ownedGamesError, setOwnedGamesError] = useState('');
  const [gameSearch, setGameSearch] = useState('');
  const [selectedGameKeys, setSelectedGameKeys] = useState([]);
  const [hideZeroHourGames, setHideZeroHourGames] = useState(true);
  const [onlyMultiplayerGames, setOnlyMultiplayerGames] = useState(true);
  const [onlyCoopGames, setOnlyCoopGames] = useState(true);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const steamInputTrimmed = useMemo(() => String(steamInput || '').trim(), [steamInput]);
  const steamCodePreview = useMemo(() => extractSteamAccountCode(steamInput), [steamInput]);

  const steamIsDirty = useMemo(() => {
    return steamInputTrimmed !== String(steamId || '').trim();
  }, [steamInputTrimmed, steamId]);

  const hasSavedSteam = useMemo(() => !!String(steamId || '').trim(), [steamId]);
  const resolvedSteamProfile = useMemo(() => normalizeSteamProfile(steamProfile), [steamProfile]);
  const steamAvatar = useMemo(() => pickSteamAvatar(steamProfile), [steamProfile]);

  const steamDisplayName = useMemo(() => {
    if (resolvedSteamProfile && resolvedSteamProfile.personaname) return resolvedSteamProfile.personaname;
    if (resolvedSteamProfile && resolvedSteamProfile.customurl) return resolvedSteamProfile.customurl;
    return '';
  }, [resolvedSteamProfile]);

  const normalizedOwnedGames = useMemo(() => normalizeOwnedGames(ownedGames), [ownedGames]);
  const gameSearchTrimmed = useMemo(() => String(gameSearch || '').trim().toLowerCase(), [gameSearch]);

  const votingCreatedAtMs = useMemo(() => {
    const closesAtMs = parseIsoMs(payload && payload.closesAt);
    const createdAtMs = parseIsoMs(payload && payload.createdAt);
    if (createdAtMs > 0) return createdAtMs;
    if (closesAtMs > 0) return Math.max(0, closesAtMs - WHAT_SHALL_WE_PLAY_WINDOW_MS);
    return 0;
  }, [payload]);

  const votingClosesAtMs = useMemo(() => {
    const closesAtMs = parseIsoMs(payload && payload.closesAt);
    if (closesAtMs > 0) return closesAtMs;
    if (votingCreatedAtMs > 0) return votingCreatedAtMs + WHAT_SHALL_WE_PLAY_WINDOW_MS;
    return 0;
  }, [payload, votingCreatedAtMs]);

  const countdownMsRemaining = useMemo(() => {
    if (!votingClosesAtMs) return 0;
    return Math.max(0, votingClosesAtMs - nowMs);
  }, [votingClosesAtMs, nowMs]);

  const voteWindowKnown = votingClosesAtMs > 0;
  const votingClosed = voteWindowKnown && countdownMsRemaining <= 0;
  const countdownLabel = useMemo(() => {
    if (!voteWindowKnown) return '';
    return formatCountdownLabel(countdownMsRemaining);
  }, [voteWindowKnown, countdownMsRemaining]);

  const visibleOwnedGames = useMemo(() => {
    const filtered = normalizedOwnedGames.filter((game) => {
      if (hideZeroHourGames && Number(game.playtime_forever || 0) <= 0) return false;
      if (getGameFilterScore(game, onlyMultiplayerGames, onlyCoopGames) < 0) return false;
      if (!gameSearchTrimmed) return true;
      return String(game.name || '').toLowerCase().includes(gameSearchTrimmed);
    });

    if (!onlyMultiplayerGames && !onlyCoopGames) return filtered;

    return [...filtered].sort((a, b) => {
      const aScore = getGameFilterScore(a, onlyMultiplayerGames, onlyCoopGames);
      const bScore = getGameFilterScore(b, onlyMultiplayerGames, onlyCoopGames);
      if (aScore !== bScore) return bScore - aScore;

      const aName = String(a.name || '').trim().toLowerCase();
      const bName = String(b.name || '').trim().toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [
    normalizedOwnedGames,
    gameSearchTrimmed,
    hideZeroHourGames,
    onlyMultiplayerGames,
    onlyCoopGames,
  ]);

  const selectedCount = useMemo(() => selectedGameKeys.length, [selectedGameKeys]);

  const selectedGames = useMemo(() => {
    if (!selectedGameKeys.length) return [];
    const selectedSet = new Set(selectedGameKeys);
    return normalizedOwnedGames.filter((game) => selectedSet.has(buildGameKey(game)));
  }, [normalizedOwnedGames, selectedGameKeys]);

  const hasUnverifiedVisibleGames = useMemo(() => {
    return visibleOwnedGames.some((game) => {
      const support = getGameSupportState(game);
      return (
        (onlyMultiplayerGames && support.multiplayer === null) ||
        (onlyCoopGames && support.coop === null)
      );
    });
  }, [visibleOwnedGames, onlyMultiplayerGames, onlyCoopGames]);

  const canSaveSteam = useMemo(() => {
    return (
      !steamLoading &&
      !steamSaving &&
      !submitBusy &&
      steamCodePreview !== null &&
      !!steamInputTrimmed &&
      steamIsDirty
    );
  }, [steamLoading, steamSaving, submitBusy, steamCodePreview, steamInputTrimmed, steamIsDirty]);

  const canSubmitVotes = useMemo(() => {
    return (
      hasSavedSteam &&
      selectedCount > 0 &&
      !steamLoading &&
      !steamSaving &&
      !submitBusy &&
      !votingClosed
    );
  }, [hasSavedSteam, selectedCount, steamLoading, steamSaving, submitBusy, votingClosed]);

  const handleClose = useCallback(() => {
    if (steamSaving || submitBusy) return;
    if (typeof onClose === 'function') onClose();
  }, [steamSaving, submitBusy, onClose]);

  const toggleGameSelected = useCallback((game) => {
    if (votingClosed) return;
    const key = buildGameKey(game);
    setSubmitError('');
    setSubmitSuccess('');
    setSelectedGameKeys((current) => {
      if (current.includes(key)) {
        return current.filter((item) => item !== key);
      }
      return [...current, key];
    });
  }, [votingClosed]);

  const applySteamPayload = useCallback((data) => {
    const savedSteamId = String((data && data.steam_id) || '').trim();
    const savedSteamProfile = normalizeSteamProfile(
      (data && data.steam_profile) || (data && data.profile) || null
    );
    const savedOwnedGames = Array.isArray(data && data.owned_games) ? data.owned_games : [];
    const savedOwnedGamesEnabled =
      data && Object.prototype.hasOwnProperty.call(data, 'owned_games_enabled')
        ? !!data.owned_games_enabled
        : true;
    const savedOwnedGamesError = String((data && data.owned_games_error) || '').trim();

    setSteamId(savedSteamId);
    setSteamInput('');
    setSteamProfile(savedSteamProfile);
    setOwnedGames(savedOwnedGames);
    setOwnedGamesEnabled(savedOwnedGamesEnabled);
    setOwnedGamesError(savedOwnedGamesError);
    setGameSearch('');
    setSelectedGameKeys([]);
    setHideZeroHourGames(true);
    setOnlyMultiplayerGames(true);
    setOnlyCoopGames(true);
    setSubmitError('');
    setSubmitSuccess('');
  }, []);

  const loadSteam = useCallback(async () => {
    setSteamLoading(true);
    setSteamError('');
    setSubmitError('');
    setSubmitSuccess('');

    try {
      const data = await apiSteamGetMe();
      applySteamPayload(data);
    } catch (e) {
      setSteamId('');
      setSteamInput('');
      setSteamProfile(null);
      setOwnedGames([]);
      setOwnedGamesEnabled(true);
      setOwnedGamesError('');
      setGameSearch('');
      setSelectedGameKeys([]);
      setHideZeroHourGames(true);
      setOnlyMultiplayerGames(true);
      setOnlyCoopGames(true);
      setSteamError(String((e && e.message) || e || 'Failed to load Steam account'));
    } finally {
      setSteamLoading(false);
    }
  }, [applySteamPayload]);

  const saveSteam = useCallback(async () => {
    setSteamError('');
    setSubmitError('');
    setSubmitSuccess('');

    const next = extractSteamAccountCode(steamInput);
    if (next === null) {
      setSteamError('Paste a valid Steam community profile URL or Steam account code.');
      return;
    }

    setSteamSaving(true);
    try {
      const saved = await apiSteamSetMe(next);
      applySteamPayload(saved);
    } catch (e) {
      setSteamError(String((e && e.message) || e || 'Failed to save Steam account'));
    } finally {
      setSteamSaving(false);
    }
  }, [applySteamPayload, steamInput]);

  const submitVotes = useCallback(async () => {
    setSubmitError('');
    setSubmitSuccess('');

    if (votingClosed) {
      setSubmitError('Voting has closed.');
      return;
    }

    if (!hasSavedSteam) {
      setSubmitError('Save your Steam account first.');
      return;
    }

    if (!selectedGames.length) {
      setSubmitError('Pick at least one game.');
      return;
    }

    const channelDbId = toInt(payload && payload.channelDbId, 0);
    if (!channelDbId) {
      setSubmitError('Missing voice channel.');
      return;
    }

    setSubmitBusy(true);
    try {
      const votePayload = buildVotePayload({
        payload,
        selectedGames,
      });

      await apiSendWhatShallWePlayVote(channelDbId, votePayload);
      setSubmitSuccess('Your votes were submitted.');
    } catch (e) {
      setSubmitError(String((e && e.message) || e || 'Failed to submit votes'));
    } finally {
      setSubmitBusy(false);
    }
  }, [hasSavedSteam, payload, selectedGames, votingClosed]);

  useEffect(() => {
    if (!open) return;
    loadSteam().catch(() => {});
  }, [open, loadSteam]);

  useEffect(() => {
    if (!open) return undefined;
    if (!voteWindowKnown) return undefined;

    setNowMs(Date.now());

    const t = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(t);
  }, [open, voteWindowKnown]);

  useEffect(() => {
    if (open) return;
    setSteamLoading(false);
    setSteamSaving(false);
    setSubmitBusy(false);
    setSteamError('');
    setSubmitError('');
    setSubmitSuccess('');
    setSteamId('');
    setSteamInput('');
    setSteamProfile(null);
    setOwnedGames([]);
    setOwnedGamesEnabled(true);
    setOwnedGamesError('');
    setGameSearch('');
    setSelectedGameKeys([]);
    setHideZeroHourGames(true);
    setOnlyMultiplayerGames(true);
    setOnlyCoopGames(true);
    setNowMs(Date.now());
  }, [open]);

  const footer = (
    <>
      <button
        type="button"
        className="coms-mini-btn"
        onClick={handleClose}
        disabled={steamSaving || submitBusy}
      >
        Close
      </button>

      {!hasSavedSteam ? (
        <button
          type="button"
          className="coms-mini-btn"
          onClick={() => saveSteam()}
          disabled={!canSaveSteam}
          style={{
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
            background: 'var(--vxs-2, #18191c)',
            color: 'var(--vx-text, #fff)',
            fontWeight: 900,
            cursor: canSaveSteam ? 'pointer' : 'not-allowed',
            opacity: canSaveSteam ? 1 : 0.65,
          }}
        >
          {steamSaving ? 'Saving…' : 'Save Steam account'}
        </button>
      ) : (
        <button
          type="button"
          className="coms-mini-btn"
          onClick={() => submitVotes()}
          disabled={!canSubmitVotes}
          style={{
            padding: '8px 10px',
            borderRadius: 10,
            border: '1px solid rgba(165,133,255,0.45)',
            background: 'rgba(165,133,255,0.16)',
            color: 'var(--vx-text, #fff)',
            fontWeight: 900,
            cursor: canSubmitVotes ? 'pointer' : 'not-allowed',
            opacity: canSubmitVotes ? 1 : 0.65,
          }}
        >
          {submitBusy ? 'Submitting…' : votingClosed ? 'Voting closed' : 'Submit games'}
        </button>
      )}
    </>
  );

  if (!open) return null;

  return (
    <ComsModal
      title="What shall we play?"
      onClose={handleClose}
      footer={footer}
      width="820px"
      maxWidth="calc(100vw - 30px)"
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <div className="coms-panel-sub" style={{ opacity: 0.92 }}>
          {String(
            (payload &&
              `${String(payload.startedByName || 'Someone').trim() || 'Someone'} started this in ${String(payload.channelName || 'voice chat').trim() || 'voice chat'}.`) ||
              'Choose the games you would play with this group.'
          )}
        </div>

        {voteWindowKnown ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              flexWrap: 'wrap',
              padding: '10px 12px',
              borderRadius: 12,
              border: votingClosed
                ? '1px solid rgba(255,120,120,0.18)'
                : '1px solid rgba(165,133,255,0.18)',
              background: votingClosed
                ? 'rgba(255,120,120,0.08)'
                : 'rgba(165,133,255,0.08)',
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: 'var(--vx-text, #fff)',
                letterSpacing: '0.02em',
              }}
            >
              {votingClosed ? 'Voting has closed' : 'Voting closes in'}
            </div>

            <div
              style={{
                fontSize: 18,
                fontWeight: 900,
                color: votingClosed ? '#ffb4b4' : '#d8c8ff',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1,
              }}
            >
              {votingClosed ? '0:00' : countdownLabel}
            </div>
          </div>
        ) : null}

        {steamLoading ? (
          <div className="coms-panel-sub" style={{ opacity: 0.85 }}>
            Loading your Steam account…
          </div>
        ) : null}

        {!steamLoading && hasSavedSteam ? (
          <div
            style={{
              display: 'grid',
              gap: 12,
              padding: '12px 14px',
              borderRadius: 12,
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700 }}>Connected Steam account</div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 12,
                  overflow: 'hidden',
                  border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
                  background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.06 * var(--vx-ov, 1)))',
                  flex: '0 0 auto',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {steamAvatar ? (
                  <img
                    src={steamAvatar}
                    alt={steamDisplayName || 'Steam avatar'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      fontSize: 24,
                      lineHeight: 1,
                      opacity: 0.85,
                    }}
                  >
                    🎮
                  </div>
                )}
              </div>

              <div style={{ minWidth: 0, display: 'grid', gap: 4 }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: 'var(--vx-text, #fff)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {steamDisplayName || 'Steam account connected'}
                </div>

                {resolvedSteamProfile && resolvedSteamProfile.profileurl ? (
                  <a
                    href={resolvedSteamProfile.profileurl}
                    target="_blank"
                    rel="noreferrer"
                    className="coms-panel-sub"
                    style={{
                      opacity: 0.86,
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    View Steam profile
                  </a>
                ) : (
                  <div className="coms-panel-sub" style={{ opacity: 0.8 }}>
                    Your Steam account is saved and ready for group game matching.
                  </div>
                )}
              </div>
            </div>

            {!resolvedSteamProfile || !resolvedSteamProfile.personaname ? (
              <div className="coms-panel-sub" style={{ opacity: 0.78 }}>
                Your Steam account is saved, but profile details could not be fully loaded right now.
              </div>
            ) : null}
          </div>
        ) : null}

        {!steamLoading && hasSavedSteam ? (
          <div
            style={{
              display: 'grid',
              gap: 10,
              padding: '12px 14px',
              borderRadius: 12,
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
              opacity: votingClosed ? 0.72 : 1,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                Your games {normalizedOwnedGames.length ? `(${normalizedOwnedGames.length})` : ''}
              </div>

              {ownedGamesEnabled && !ownedGamesError && normalizedOwnedGames.length ? (
                <div className="coms-panel-sub" style={{ opacity: 0.78 }}>
                  {selectedCount ? `${selectedCount} selected` : 'Pick the games you would play'}
                </div>
              ) : null}
            </div>

            {ownedGamesEnabled && !ownedGamesError && normalizedOwnedGames.length ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr)',
                  gap: 8,
                }}
              >
                <input
                  type="text"
                  value={gameSearch}
                  onChange={(e) => setGameSearch(String(e.target.value || ''))}
                  placeholder="Search your games"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={votingClosed}
                  style={{
                    background: 'var(--vxs-1, #111214)',
                    color: 'var(--vx-text, #fff)',
                    border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                    borderRadius: 8,
                    padding: '9px 11px',
                    width: '100%',
                    boxSizing: 'border-box',
                    fontSize: 13,
                    opacity: votingClosed ? 0.7 : 1,
                  }}
                />

                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      color: 'var(--vx-text, #fff)',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: votingClosed ? 'default' : 'pointer',
                      userSelect: 'none',
                      opacity: votingClosed ? 0.72 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={hideZeroHourGames}
                      onChange={(e) => setHideZeroHourGames(!!e.target.checked)}
                      disabled={votingClosed}
                      style={{
                        width: 14,
                        height: 14,
                        margin: 0,
                        accentColor: '#a585ff',
                        cursor: votingClosed ? 'default' : 'pointer',
                      }}
                    />
                    Hide 0 hours played games
                  </label>

                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      color: 'var(--vx-text, #fff)',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: votingClosed ? 'default' : 'pointer',
                      userSelect: 'none',
                      opacity: votingClosed ? 0.72 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={onlyMultiplayerGames}
                      onChange={(e) => setOnlyMultiplayerGames(!!e.target.checked)}
                      disabled={votingClosed}
                      style={{
                        width: 14,
                        height: 14,
                        margin: 0,
                        accentColor: '#a585ff',
                        cursor: votingClosed ? 'default' : 'pointer',
                      }}
                    />
                    Only multiplayer
                  </label>

                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      color: 'var(--vx-text, #fff)',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: votingClosed ? 'default' : 'pointer',
                      userSelect: 'none',
                      opacity: votingClosed ? 0.72 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={onlyCoopGames}
                      onChange={(e) => setOnlyCoopGames(!!e.target.checked)}
                      disabled={votingClosed}
                      style={{
                        width: 14,
                        height: 14,
                        margin: 0,
                        accentColor: '#a585ff',
                        cursor: votingClosed ? 'default' : 'pointer',
                      }}
                    />
                    Only co-op
                  </label>
                </div>

                {(onlyMultiplayerGames || onlyCoopGames) && hasUnverifiedVisibleGames ? (
                  <div className="coms-panel-sub" style={{ opacity: 0.78 }}>
                    Some games are still shown as unverified where Steam category lookup could not confirm support.
                  </div>
                ) : null}
              </div>
            ) : null}

            {!ownedGamesEnabled ? (
              <div className="coms-panel-sub" style={{ opacity: 0.82 }}>
                Steam game list is not enabled on this server yet.
              </div>
            ) : null}

            {ownedGamesEnabled && ownedGamesError ? (
              <div className="coms-panel-sub" style={{ color: '#ffb4b4' }}>
                {ownedGamesError}
              </div>
            ) : null}

            {ownedGamesEnabled && !ownedGamesError && !normalizedOwnedGames.length ? (
              <div className="coms-panel-sub" style={{ opacity: 0.82 }}>
                No visible Steam games were found for this account.
              </div>
            ) : null}

            {ownedGamesEnabled && !ownedGamesError && normalizedOwnedGames.length && !visibleOwnedGames.length ? (
              <div className="coms-panel-sub" style={{ opacity: 0.82 }}>
                No games match that filter.
              </div>
            ) : null}

            {ownedGamesEnabled && !ownedGamesError && visibleOwnedGames.length ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
                  gap: 8,
                  maxHeight: 360,
                  overflowY: 'auto',
                  paddingRight: 4,
                  alignItems: 'stretch',
                }}
              >
                {visibleOwnedGames.map((game) => {
                  const key = buildGameKey(game);
                  const isSelected = selectedGameKeys.includes(key);
                  const gameTags = buildGameTags(game);

                  return (
                    <button
                      key={`${game.appid}-${game.name || 'game'}`}
                      type="button"
                      onClick={() => toggleGameSelected(game)}
                      disabled={votingClosed}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '18px minmax(0, 1fr)',
                        alignItems: 'start',
                        gap: 9,
                        width: '100%',
                        textAlign: 'left',
                        padding: '9px 10px',
                        borderRadius: 10,
                        border: isSelected
                          ? '1px solid rgba(165,133,255,0.65)'
                          : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.08 * var(--vx-ov, 1)))',
                        background: isSelected ? 'rgba(165,133,255,0.10)' : 'rgba(0,0,0,0.18)',
                        cursor: votingClosed ? 'default' : 'pointer',
                        minHeight: 62,
                        opacity: votingClosed ? 0.72 : 1,
                      }}
                    >
                      <div
                        aria-hidden="true"
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 5,
                          border: isSelected
                            ? '1px solid rgba(165,133,255,0.85)'
                            : '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.22 * var(--vx-ov, 1)))',
                          background: isSelected ? 'rgba(165,133,255,0.9)' : 'transparent',
                          color: '#fff',
                          display: 'grid',
                          placeItems: 'center',
                          fontSize: 12,
                          lineHeight: 1,
                          marginTop: 1,
                        }}
                      >
                        {isSelected ? '✓' : ''}
                      </div>

                      <div
                        style={{
                          minWidth: 0,
                          display: 'grid',
                          gap: 3,
                        }}
                      >
                        <div
                          style={{
                            color: 'var(--vx-text, #fff)',
                            fontWeight: 700,
                            fontSize: 12,
                            lineHeight: 1.25,
                            wordBreak: 'break-word',
                          }}
                          title={game.name || 'Unknown game'}
                        >
                          {game.name || 'Unknown game'}
                        </div>

                        <div
                          className="coms-panel-sub"
                          style={{
                            opacity: 0.74,
                            fontSize: 11,
                            lineHeight: 1.2,
                          }}
                          title={`${game.playtime_forever} minutes played`}
                        >
                          {formatPlaytime(game.playtime_forever)} played
                        </div>

                        {gameTags.length ? (
                          <div
                            className="coms-panel-sub"
                            style={{
                              opacity: 0.62,
                              fontSize: 10.5,
                              lineHeight: 1.2,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                            }}
                          >
                            {gameTags.join(' · ')}
                          </div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}

        {!steamLoading && !hasSavedSteam ? (
          <div
            style={{
              display: 'grid',
              gap: 10,
              padding: '12px 14px',
              borderRadius: 12,
              border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.10 * var(--vx-ov, 1)))',
              background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700 }}>Add Steam account</div>

            <input
              type="text"
              value={steamInput}
              onChange={(e) => {
                setSteamError('');
                setSubmitError('');
                setSubmitSuccess('');
                setSteamInput(String(e.target.value || ''));
              }}
              disabled={steamSaving || submitBusy}
              placeholder="Paste Steam profile URL or account code"
              autoComplete="off"
              spellCheck={false}
              style={{
                background: 'var(--vxs-1, #111214)',
                color: 'var(--vx-text, #fff)',
                border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))',
                borderRadius: 8,
                padding: '10px 12px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />

            {steamInputTrimmed ? (
              steamCodePreview === null ? (
                <div className="coms-panel-sub" style={{ color: '#ffb4b4' }}>
                  Paste a Steam community profile URL or a valid Steam account code.
                </div>
              ) : (
                <div className="coms-panel-sub" style={{ opacity: 0.9 }}>
                  Will save as <span style={{ opacity: 1 }}>{steamCodePreview}</span>.
                </div>
              )
            ) : null}
          </div>
        ) : null}

        {steamError ? (
          <div className="coms-panel-sub" style={{ color: '#ffb4b4' }}>
            {steamError}
          </div>
        ) : null}

        {submitError ? (
          <div className="coms-panel-sub" style={{ color: '#ffb4b4' }}>
            {submitError}
          </div>
        ) : null}

        {submitSuccess ? (
          <div className="coms-panel-sub" style={{ color: '#b8ffcf' }}>
            {submitSuccess}
          </div>
        ) : null}

        <div className="coms-panel-sub" style={{ opacity: 0.82, lineHeight: 1.45 }}>
          We only save your Steam public profile code. We do not keep passwords, login tokens, or
          private account details. Your Steam profile and game details must be public for this feature
          to work.
        </div>
      </div>
    </ComsModal>
  );
}